import { useEffect, useRef, useState } from 'react';
import styles from './DeleteAssetModal.module.css';

export interface DeleteAssetModalProps {
  open: boolean;
  /** Title of the asset being deleted — shown in the heading. */
  assetTitle: string;
  /** True when the asset has uploaded versions. Drives whether the
   *  cloud-cleanup checkbox is rendered at all. */
  hasVersions: boolean;
  onCancel(): void;
  /** Submitter passes whether the user opted into the cloud-side delete.
   *  Always false when `hasVersions` is false. */
  onConfirm(opts: { deleteCloud: boolean }): void;
}

/**
 * Confirm modal for deleting a custom asset from the working session.
 *
 * Replaces the prior `window.confirm` × 2 flow. The default action is now
 * LOCAL-ONLY — talent removes the asset from their session without nuking
 * the deliverables already shipped to the client. Cloud cleanup is opt-in
 * via a checkbox that only appears when there is something to clean up.
 */
export function DeleteAssetModal({
  open,
  assetTitle,
  hasVersions,
  onCancel,
  onConfirm,
}: DeleteAssetModalProps) {
  const [deleteCloud, setDeleteCloud] = useState(false);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  // Reset checkbox + focus Cancel each time the modal opens.
  useEffect(() => {
    if (open) {
      setDeleteCloud(false);
      // Defer focus so the button is mounted before we grab it.
      const id = window.setTimeout(() => cancelBtnRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Esc closes without action.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const title = assetTitle.trim() || 'Untitled';
  // Only honor the checkbox if there's actually something cloud-side to clean.
  const effectiveDeleteCloud = hasVersions && deleteCloud;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-asset-modal-title"
      >
        <div className={styles.headerRow}>
          <h2 id="delete-asset-modal-title" className={styles.title}>
            Delete asset &ldquo;{title}&rdquo;?
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onCancel}
            aria-label="Close"
          >
            <i className="ph-bold ph-x" aria-hidden="true" />
          </button>
        </div>

        <p className={styles.info}>
          This will remove the asset from your current session.
          {hasVersions && !deleteCloud && (
            <>
              {' '}Uploaded files at TRO will be left untouched.
            </>
          )}
        </p>

        {hasVersions && (
          <>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={deleteCloud}
                onChange={(e) => setDeleteCloud(e.target.checked)}
              />
              <span>Also delete uploaded files at TRO</span>
            </label>
            {deleteCloud && (
              <p className={styles.warning}>
                The asset folder and all uploaded versions will be permanently
                deleted from the cloud. This cannot be undone.
              </p>
            )}
          </>
        )}

        <div className={styles.actions}>
          <button
            ref={cancelBtnRef}
            type="button"
            className={styles.btnGhost}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={
              effectiveDeleteCloud ? styles.btnDanger : styles.btnPrimary
            }
            onClick={() => onConfirm({ deleteCloud: effectiveDeleteCloud })}
          >
            <i className="ph-bold ph-trash" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
